'use client';

import { Radio, RadioGroup } from '@headlessui/react';
import { ReactNode, useEffect, useState } from 'react';
import { BadgeWithBorder } from '../../components/Badges';
import { cn } from '../../lib/utils';

type BadgeOptions = {
  text: string;
  color?: string;
  variant?: 'default' | 'filled' | 'muted-filled';
  size?: 'default' | 'xs' | 'sm';
  textColor?: string; // Tailwind text color class, e.g., 'text-red-500'
  bgColor?: string; // Tailwind background color class, e.g., 'bg-red-100'
  icon?: React.ReactNode; // Optional icon for the badge
  className?: string; // Optional custom class for the badge
};

type Option = {
  name: string;
  key: string | number;
  icon?: React.ReactNode; // Optional icon
  rightElement?: React.ReactNode;
  label?: string; // Optional label (e.g. for Magic Optimise)
  badge?: string | BadgeOptions;
  children?: ReactNode; // Optional children
  description?: ReactNode; // Optional description text
};

export interface RadioButtonProps {
  options: Option[];
  initialValueKey: string | number;
  onChange: (value: Option) => void;
  className?: string;
  isSelectable?: boolean;
  variant?: 'default' | 'horizontal' | 'vertical' | 'xs'; // Add xs variant
  size?: 'default' | 'xs' | 'sm'; // Add size prop with xs option
}

// Define color mappings for each badge key
const badgeColorMap = {
  daily: { textColor: 'text-slate-800', bgColor: 'bg-slate-50' },
  weekly: { textColor: 'text-blue-700', bgColor: 'bg-blue-50' },
  fortnightly: { textColor: 'text-sky-700', bgColor: 'bg-sky-50' },
  monthly: { textColor: 'text-cyan-800', bgColor: 'bg-cyan-50' },
  custom: { textColor: 'text-amber-800', bgColor: 'bg-amber-50' },
};

export function RadioButtons({
  options,
  initialValueKey,
  onChange,
  className,
  isSelectable = true,
  variant = 'default',
  size = 'default',
}: RadioButtonProps) {
  // Use null instead of undefined to ensure the component is always controlled
  const [selectedOption, setSelectedOption] = useState<Option | null>(
    options.find((option) => option.key === initialValueKey) ?? null
  );

  useEffect(() => {
    const initialOption = options.find(
      (option) => option.key === initialValueKey
    );

    // Only update state if the selected option has actually changed
    if (
      initialOption &&
      (!selectedOption || selectedOption.key !== initialOption.key)
    ) {
      setSelectedOption(initialOption);
    } else if (!initialOption && selectedOption !== null) {
      // If initialOption becomes undefined (e.g., props change), reset to null
      setSelectedOption(null);
    }
  }, [initialValueKey, options, selectedOption]);

  const handleChange = (value: Option) => {
    setSelectedOption(value);
    if (onChange) {
      onChange(value);
    }
  };

  // Unified icon sizing configuration - resolves conflicts between variant and size
  const getIconSize = () => {
    // Priority: size prop > variant fallback
    if (size === 'xs') return 'w-3.5 h-3.5'; // Slightly larger for better visibility
    if (size === 'sm') return 'w-4 h-4';
    if (variant === 'xs') return 'w-4 h-4'; // Unified with size logic
    return 'w-5 h-5'; // default
  };

  // Helper function to render icons with consistent sizing
  const renderIcon = (icon: ReactNode) => {
    if (!icon) return null;

    return (
      <div
        className={cn(
          'flex items-center justify-center flex-shrink-0 text-current',
          getIconSize(),
          // Add overflow protection and minimum spacing
          'overflow-visible p-0.5'
        )}
      >
        {icon}
      </div>
    );
  };

  return (
    <RadioGroup
      value={selectedOption}
      onChange={handleChange}
      className={cn(
        variant === 'horizontal'
          ? 'flex flex-row gap-2 w-auto'
          : variant === 'vertical'
            ? 'flex flex-col gap-2'
            : variant === 'xs'
              ? 'flex flex-col gap-1.5'
              : 'flex flex-col gap-2',
        size === 'xs' && 'flex-wrap gap-1.5 text-[11px]',
        className
      )}
    >
      {options.map((option) => (
        <div
          key={option.key}
          className={cn(variant === 'horizontal' && 'relative')}
        >
          <Radio
            value={option}
            className={cn(
              'cursor-pointer transition-colors duration-200 ease-in-out focus:outline-none',
              'flex items-center justify-between flex-1',
              'rounded-lg bg-white text-slate-800',
              'ring-[1px] ring-slate-200/20 shadow-[2px_2px_5px_rgba(0,0,0,0.05),_-2px_-2px_5px_rgba(255,255,255,0.8)]',
              'hover:bg-slate-50/80 transition duration-200 ease-in-out',
              // Simplified sizing logic
              size === 'xs'
                ? 'px-2 py-1 text-xs font-bold cursor-pointer h-[28px] box-border w-full' // Slightly taller for icons
                : 'px-2.5 py-2.5 text-sm',
              selectedOption?.key === option.key
                ? size === 'xs'
                  ? 'bg-green-50 text-green-900 ring-green-800 ring-2'
                  : 'bg-green-50 ring-green-800 ring-2'
                : '',
              !isSelectable &&
                'cursor-pointer hover:bg-slate-50/80 transition duration-200 ease-in-out',
              'data-[checked]:bg-green-100 data-[checked]:ring-green-800 data-[checked]:ring-2'
            )}
            onClick={
              !isSelectable && option.rightElement
                ? (e) => {
                    e.preventDefault();
                    handleChange(option);
                  }
                : undefined
            }
          >
            <div className="inline-flex items-center w-full">
              <div
                className={cn(
                  'flex items-center',
                  option.icon
                    ? 'space-x-2 justify-start' // Consistent spacing for all sizes
                    : size === 'xs'
                      ? 'w-full justify-center'
                      : 'w-full justify-center space-x-1'
                )}
              >
                {option.icon && renderIcon(option.icon)}
                <div
                  className={cn(
                    'flex flex-col',
                    !option.icon && size === 'xs' && 'w-full text-center'
                  )}
                >
                  <span
                    className={cn(
                      'font-bold',
                      size === 'xs' ? 'text-[11px]' : '',
                      'break-words'
                    )}
                  >
                    {option.name}
                  </span>
                  {option.label && (
                    <span className="text-xs text-slate-600">
                      {option.label}
                    </span>
                  )}
                  {option.description && (
                    <span className="text-xs text-slate-600">
                      {option.description}
                    </span>
                  )}
                </div>
              </div>

              {option.badge && (
                <>
                  {typeof option.badge === 'string' ? (
                    <div
                      className={cn(
                        'inline-flex items-center rounded-lg text-xs bg-slate-100 text-slate-800 ml-1',
                        size === 'xs' ? 'px-1 py-0.5' : 'px-1.5 py-0.5'
                      )}
                    >
                      {option.badge}
                    </div>
                  ) : option.badge.color ? (
                    <BadgeWithBorder
                      color={option.badge.color || 'slate'}
                      variant={option.badge.variant || 'default'}
                      size={option.badge.size || 'xs'}
                      className={cn(option.badge.className, 'ml-1')}
                      iconLeft={option.badge.icon}
                    >
                      {option.badge.text}
                    </BadgeWithBorder>
                  ) : (
                    <div
                      className={cn(
                        'inline-flex items-center rounded-lg text-xs ml-1',
                        size === 'xs' ? 'px-1 py-0.5' : 'px-1.5 py-0.5',
                        option.badge.className ||
                          cn(
                            option.badge.textColor ||
                              badgeColorMap[
                                option.key as keyof typeof badgeColorMap
                              ]?.textColor ||
                              'text-slate-800',
                            option.badge.bgColor ||
                              badgeColorMap[
                                option.key as keyof typeof badgeColorMap
                              ]?.bgColor ||
                              'bg-slate-100'
                          )
                      )}
                    >
                      {option.badge.icon && (
                        <span className="mr-1 text-current">
                          {option.badge.icon}
                        </span>
                      )}
                      {option.badge.text}
                    </div>
                  )}
                </>
              )}

              <div
                className={cn(
                  'flex-1',
                  !option.icon && size === 'xs' && 'hidden'
                )}
              ></div>

              {option.rightElement && (
                <div className="flex items-center ml-auto">
                  {option.rightElement}
                </div>
              )}
            </div>
          </Radio>
          {selectedOption?.key === option.key && option.children && (
            <div
              className={cn(
                'w-full',
                variant === 'horizontal' && 'absolute left-0 right-0 mt-2'
              )}
            >
              {option.children}
            </div>
          )}
        </div>
      ))}
    </RadioGroup>
  );
}
